'use client';

import Link from 'next/link';
import { GraduationCap, Briefcase, Users, Building2, Church, Shield, ChevronRight } from 'lucide-react';
import { InstitutionConnection, Institution } from '@/lib/types';
import { cn } from '@/lib/utils';

interface InstitutionTagsProps {
  institutions: InstitutionConnection[];
}

const institutionIcons: Record<Institution['type'], React.ReactNode> = {
  school: <GraduationCap className="w-5 h-5" />,
  university: <GraduationCap className="w-5 h-5" />,
  company: <Briefcase className="w-5 h-5" />,
  club: <Users className="w-5 h-5" />,
  military: <Shield className="w-5 h-5" />,
  church: <Church className="w-5 h-5" />,
  other: <Building2 className="w-5 h-5" />,
};

const institutionStyles: Record<Institution['type'], string> = {
  school: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100',
  university: 'bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100',
  company: 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100',
  club: 'bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100',
  military: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100',
  church: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100',
  other: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100',
};

export function InstitutionTags({ institutions }: InstitutionTagsProps) {
  if (!institutions || institutions.length === 0) return null;

  const formatYears = (connection: InstitutionConnection): string => {
    if (connection.graduationYear) {
      return connection.institution.type === 'school' || connection.institution.type === 'university'
        ? `Abschluss ${connection.graduationYear}`
        : `${connection.yearStart || '?'} – ${connection.yearEnd || 'heute'}`;
    }
    if (!connection.yearStart && !connection.yearEnd) return '';
    if (connection.yearStart && !connection.yearEnd) return `seit ${connection.yearStart}`;
    return `${connection.yearStart} – ${connection.yearEnd}`;
  };

  return (
    <section className="memorial-section bg-memorial-muted/30">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary text-center mb-4">
          Bildung & Beruf
        </h2>
        <p className="text-memorial-text-secondary text-center mb-10 max-w-2xl mx-auto">
          Stationen im Leben
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {institutions.map((connection) => (
            <Link
              key={connection.id}
              href={`/search?institution=${connection.institutionId}`}
              className={cn(
                'group flex items-start gap-4 p-5 rounded-xl border transition-all duration-200',
                institutionStyles[connection.institution.type]
              )}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                {institutionIcons[connection.institution.type]}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-lg leading-tight mb-1">
                  {connection.institution.name}
                </h3>

                {connection.role && (
                  <p className="text-sm opacity-80 mb-1">{connection.role}</p>
                )}

                <p className="text-sm opacity-70">
                  {formatYears(connection)}
                  {connection.department && ` • ${connection.department}`}
                </p>

                {connection.institution.memorialCount && connection.institution.memorialCount > 1 && (
                  <p className="text-xs mt-2 opacity-60 flex items-center gap-1 group-hover:opacity-100 transition-opacity">
                    <span>{connection.institution.memorialCount} weitere Gedenkseiten</span>
                    <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
